import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, Save, FileText, BookOpen, Newspaper, Download, Copy, Sparkles, ImagePlus, Loader2, X, Search } from "lucide-react";
import { CourseCard, AdminPager } from "@/components/admin/AdminCourseList";
import { AttachmentsField, FileUploadField } from "@/components/admin/AttachmentsField";

const PAGE_SIZE = 10;

const TESTIDS = {
  root: "admin-content-root",
  tab: (k) => `admin-content-tab-${k}`,
  newBtn: (k) => `admin-content-new-${k}`,
  editBtn: "admin-content-edit-btn",
  duplicateBtn: "admin-content-duplicate-btn",
  deleteBtn: "admin-content-delete-btn",
  saveBtn: "admin-content-save-btn",
  courseExpand: (id) => `admin-course-expand-${id}`,
};

/**
 * Admin CMS — 4 collections managed with the same generic form dialog:
 *   - courses   /api/admin/content/courses
 *   - lessons   /api/admin/content/lessons
 *   - articles  /api/admin/content/articles
 *   - resources /api/admin/content/resources (downloadable docs/PDFs)
 *
 * Every schema is described declaratively at the top of the file. Adding
 * a new field is a one-line change.
 */
const SCHEMAS = {
  courses: {
    label: "Courses",
    icon: BookOpen,
    endpoint: "/admin/content/courses",
    fields: [
      { name: "title", type: "text", required: true },
      { name: "summary", type: "textarea", label: "Summary (short tagline shown on the course card)" },
      { name: "language", type: "select", options: ["en", "es", "fr"], default: "en" },
      { name: "level", type: "select", options: ["beginner", "intermediate", "advanced"], default: "beginner" },
      { name: "tier", type: "select", options: ["free", "basic", "premium"], default: "free",
        label: "Access tier (Basic unlocks for Basic + Premium members; Premium is Premium-only)" },
      { name: "cover_image", type: "image-upload", label: "Cover image" },
      { name: "duration", type: "text",
        label: "Duration (free text — e.g. \"3.5 hr · 80 mins\")",
        placeholder: "3.5 hr · 80 mins" },
      { name: "featured_for_free", type: "toggle", label: "Featured for Free tier (shows on the free 6-shelf, one of the 2 clickable slots)", default: false },
      { name: "order", type: "number", default: 100 },
    ],
    columns: ["title", "language", "level", "tier", "order"],
  },
  lessons: {
    label: "Lessons",
    icon: FileText,
    endpoint: "/admin/content/lessons",
    // Hidden from the top tab bar — lessons now live inline under their
    // parent course on the Courses tab. Kept in SCHEMAS so EditorDialog
    // can still open a lesson form for inline add / edit. Flip to false
    // if you ever need the standalone flat list back.
    hiddenTab: true,
    fields: [
      { name: "course_id", type: "course-select", required: true, label: "Course" },
      // Virtual field: smart chapter picker drives chapter_number +
      // chapter_title + chapter_tier together so admins can't typo one
      // of the three and silently fragment a chapter.
      { name: "__chapter_picker__", type: "chapter-picker", label: "Chapter",
        help: "Pick an existing chapter in this course to add this lesson to it, or create a new chapter. All three chapter fields (number, title, tier) stay in sync automatically." },
      // These are populated by the chapter-picker above — never shown
      // directly to prevent inconsistent values across sibling lessons.
      { name: "chapter_number", type: "hidden", default: 1 },
      { name: "chapter_title", type: "hidden" },
      { name: "chapter_tier", type: "hidden", default: "free" },
      // ── Lesson content ─────────────────────────────────────────
      { name: "__section_lesson__", type: "section-header", label: "Lesson content" },
      { name: "language", type: "select", options: ["en", "es", "fr"], default: "en" },
      { name: "title", type: "text", required: true, label: "Lesson title" },
      { name: "overview", type: "textarea", rows: 10,
        label: "Overview (shown under the video on the lesson page — markdown supported)" },
      { name: "video_url", type: "text", label: "Video URL (Vimeo, YouTube, Google Drive, or direct MP4)", placeholder: "e.g. https://vimeo.com/123456789" },
      { name: "tier", type: "select", options: ["free", "basic", "premium"], default: "free",
        label: "Access tier for this lesson (usually matches the chapter's tier)" },
      { name: "free_preview", type: "toggle", label: "Free preview (any signed-in user can watch regardless of tier)", default: false },
      { name: "order", type: "number", default: 100, label: "Order within chapter" },
      { name: "duration_minutes", type: "number", label: "Duration (minutes)" },
      { name: "attachments", type: "attachments", default: [], label: "Attachments — worksheets, PDFs, chart JPGs (upload or paste a link)" },
    ],
    columns: ["title", "course_title", "chapter_number", "tier", "language"],
  },
  articles: {
    label: "Articles",
    icon: Newspaper,
    endpoint: "/admin/content/articles",
    fields: [
      { name: "title", type: "text", required: true },
      { name: "slug", type: "text" },
      { name: "category", type: "text", default: "general" },
      { name: "language", type: "select", options: ["en", "es", "fr"], default: "en" },
      { name: "tier", type: "select", options: ["free", "basic", "premium"], default: "free" },
      { name: "cover_image", type: "image-upload", label: "Cover image" },
      { name: "excerpt", type: "textarea", rows: 3 },
      { name: "body", type: "textarea", rows: 12, label: "Body (markdown)", required: true },
      { name: "read_time", type: "text", label: "Read time (e.g. \"5 min read\")", placeholder: "5 min read" },
      { name: "featured_for_free", type: "toggle", label: "Featured for Free tier (shows on the free 6-shelf, one of the 2 clickable slots)", default: false },
    ],
    columns: ["title", "category", "tier", "language"],
  },
  resources: {
    label: "Resources / Documents",
    icon: Download,
    endpoint: "/admin/content/resources",
    fields: [
      { name: "title", type: "text", required: true },
      { name: "description", type: "textarea", rows: 3 },
      { name: "category", type: "text", default: "worksheet" },
      { name: "tier", type: "select", options: ["free", "basic", "premium"], default: "free" },
      { name: "file_url", type: "file-upload", required: true, label: "File — upload (PDF, JPG, MT4/MT5 .tpl/.set/.ex5/.mq5, TradingView .json/.pine, .zip, .csv, .xlsx) or paste a link" },
      { name: "file_type", type: "text", default: "pdf", label: "File type label (auto-filled on upload)" },
      { name: "order", type: "number", default: 100 },
    ],
    columns: ["title", "category", "tier", "file_type"],
  },
};

function ImageUploadField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const inputId = `img-upload-${Math.random().toString(36).slice(2, 8)}`;

  const upload = async (file) => {
    if (!file) return;
    const okTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Use JPG, PNG, GIF, or WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB — compress the image first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const relUrl = r.data?.url;
      if (!relUrl) throw new Error("No URL returned");
      // Store with the backend origin so <img src> resolves no matter
      // where the frontend is served from.
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const absoluteUrl = relUrl.startsWith("http") ? relUrl : `${base}${relUrl}`;
      onChange(absoluteUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return (
      <div className="mt-1 flex items-start gap-3">
        <img
          src={value}
          alt="cover"
          className="w-32 h-20 object-cover rounded-md border border-slate-200 bg-slate-50"
          onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
        />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="text-xs text-slate-500 truncate font-mono" title={value}>
            {value}
          </div>
          <div className="flex items-center gap-3">
            <label
              htmlFor={inputId}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 cursor-pointer"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
              {busy ? "Uploading…" : "Replace"}
            </label>
            <button
              type="button"
              onClick={() => onChange("")}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700"
            >
              <X size={12} /> Remove
            </button>
          </div>
        </div>
        <input
          id={inputId}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="sr-only"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-2">
      <label
        htmlFor={inputId}
        className="flex flex-col items-center justify-center gap-2 h-28 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 hover:bg-slate-100 hover:border-blue-400 cursor-pointer transition-colors"
      >
        {busy ? (
          <>
            <Loader2 size={20} className="animate-spin text-slate-500" />
            <span className="text-xs text-slate-500">Uploading…</span>
          </>
        ) : (
          <>
            <ImagePlus size={20} className="text-slate-500" />
            <span className="text-xs text-slate-600 font-semibold">Click to upload cover image</span>
            <span className="text-[10px] text-slate-400">JPG, PNG, GIF or WebP · max 5MB</span>
          </>
        )}
        <input
          id={inputId}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="sr-only"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </label>
      {showUrlInput ? (
        <div className="flex items-center gap-2">
          <input
            type="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://example.com/cover.jpg"
            className="flex-1 h-9 px-2 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            data-testid="image-url-paste-input"
          />
          <button
            type="button"
            onClick={() => {
              const trimmed = urlDraft.trim();
              if (!trimmed) return;
              if (!/^https?:\/\//i.test(trimmed)) {
                toast.error("URL must start with http:// or https://");
                return;
              }
              onChange(trimmed);
              setUrlDraft("");
              setShowUrlInput(false);
            }}
            className="h-9 px-3 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800"
            data-testid="image-url-paste-save"
          >
            Use URL
          </button>
          <button
            type="button"
            onClick={() => { setShowUrlInput(false); setUrlDraft(""); }}
            className="h-9 px-2 text-xs text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowUrlInput(true)}
          className="text-xs font-semibold text-blue-700 hover:text-blue-800"
          data-testid="image-url-paste-toggle"
        >
          Or paste an image URL
        </button>
      )}
    </div>
  );
}

export default function AdminContent() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("courses");
  const schema = SCHEMAS[tab];

  return (
    <div data-testid={TESTIDS.root} className="space-y-6">
      <div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight">{t("admin.content.title")}</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">{t("admin.content.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          {Object.entries(SCHEMAS).filter(([_, s]) => !s.hiddenTab).map(([k, s]) => (
            <TabsTrigger key={k} value={k} data-testid={TESTIDS.tab(k)}>
              <s.icon size={14} className="mr-1.5" /> {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(SCHEMAS).filter(([_, s]) => !s.hiddenTab).map(([k, s]) => (
          <TabsContent key={k} value={k} className="mt-4">
            {tab === k && <CollectionView schemaKey={k} schema={s} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CollectionView({ schemaKey, schema }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {...doc} = edit
  const [language, setLanguage] = useState("all");
  const hasLanguage = schema.fields.some((f) => f.name === "language");
  // Lessons need to display / pick their parent course by TITLE, not ID.
  // We load the course list once and build a { id -> title } map so the
  // table can render a human-readable column and the form can offer a
  // dropdown instead of a raw ObjectId input.
  const needsCourses = schemaKey === "lessons";
  const [courses, setCourses] = useState([]);
  const courseMap = useMemo(() => {
    const m = new Map();
    courses.forEach((c) => m.set(c.id, c.title));
    return m;
  }, [courses]);
  useEffect(() => {
    if (!needsCourses) return;
    api.get("/admin/content/courses").then(({ data }) => setCourses(data || [])).catch(() => {});
  }, [needsCourses]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (hasLanguage && language !== "all") params.set("language", language);
    const qs = params.toString();
    api.get(`${schema.endpoint}${qs ? `?${qs}` : ""}`).then(({ data }) => setItems(data)).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    });
  }, [schema.endpoint, hasLanguage, language]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("admin.content.deleteConfirm"))) return;
    try {
      await api.delete(`${schema.endpoint}/${id}`);
      toast.success(t("admin.content.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const emptyDoc = () => {
    const doc = {};
    schema.fields.forEach((f) => { if (f.default !== undefined) doc[f.name] = f.default; });
    return doc;
  };

  // Clone: strip server-managed fields and the id, keep everything else so
  // the editor opens in "create" mode with the source content pre-filled.
  // Suffix the title so the admin doesn't accidentally save an identical
  // duplicate over the original.
  const duplicate = (row) => {
    const clone = { ...row };
    ["id", "created_at", "updated_at", "published_at"].forEach((k) => delete clone[k]);
    if (clone.title) clone.title = `${clone.title} (copy)`;
    if (clone.slug)  clone.slug = "";  // let the server regenerate
    setEditing(clone);
  };

  // Lessons-only inline preview: when we're on the Courses tab, we group
  // all lessons by their course_id so each course row can expand and show
  // its child lessons. Loaded once on tab mount.
  const isCoursesTab = schemaKey === "courses";
  const [allLessons, setAllLessons] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  // Dedicated editor state for inline lesson add/edit from the Courses tab.
  // Routes through SCHEMAS.lessons so saves hit the lessons endpoint even
  // though we're rendered inside the courses CollectionView.
  const [lessonEditing, setLessonEditing] = useState(null);
  const reloadLessons = useCallback(() => {
    api.get("/admin/content/lessons").then(({ data }) => setAllLessons(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!isCoursesTab) return;
    reloadLessons();
  }, [isCoursesTab, items.length, reloadLessons]);
  const lessonsByCourse = useMemo(() => {
    const m = new Map();
    allLessons.forEach((l) => {
      const arr = m.get(l.course_id) || [];
      arr.push(l);
      m.set(l.course_id, arr);
    });
    // Sort: chapter → order → title so the tree reads left-to-right.
    for (const arr of m.values()) {
      arr.sort((a, b) =>
        (a.chapter_number ?? 0) - (b.chapter_number ?? 0) ||
        (a.order ?? 0) - (b.order ?? 0) ||
        (a.title || "").localeCompare(b.title || ""),
      );
    }
    return m;
  }, [allLessons]);
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  // Open the inline lesson editor pre-filled with the parent course's id
  // and language so admins don't retype context.
  const addLessonToCourse = (course) => {
    const seed = {};
    SCHEMAS.lessons.fields.forEach((f) => { if (f.default !== undefined) seed[f.name] = f.default; });
    seed.course_id = course.id;
    seed.language = course.language || "en";
    // Default order: next slot in the current lesson list (last + 10).
    const siblings = lessonsByCourse.get(course.id) || [];
    const maxOrder = siblings.reduce((m, l) => Math.max(m, l.order ?? 0), 0);
    seed.order = maxOrder + 10;
    setLessonEditing(seed);
  };
  const removeLesson = async (lesson) => {
    if (!window.confirm(`Delete lesson "${lesson.title || "(untitled)"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/content/lessons/${lesson.id}`);
      toast.success("Lesson deleted");
      reloadLessons();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const refreshCourseData = () => {
    load();
    api.get("/admin/content/courses").then(({ data }) => setCourses(data || []));
    api.get("/admin/content/lessons").then(({ data }) => setAllLessons(data || []));
  };

  // Clone course + lessons + quizzes into target languages; translation runs in the background.
  const translateCourse = async (course, targets) => {
    const toastId = toast.loading(`Starting translation of ${course.title}…`);
    try {
      const { data } = await api.post(`/admin/content/courses/${course.id}/duplicate-language`, { target_languages: targets });
      const created = data.created || [];
      const fresh = created.filter((c) => !c.skipped);
      if (fresh.length) {
        const langs = fresh.map((c) => c.language.toUpperCase()).join(", ");
        toast.success(`Translating "${course.title}" into ${langs}. Progress shows on the new course card${hasLanguage && language !== "all" ? " (switch the language filter to see it)" : ""}.`, { id: toastId, duration: 7000 });
      } else {
        toast.dismiss(toastId);
      }
      const skipped = created.filter((c) => c.skipped);
      if (skipped.length) {
        toast.info(`${skipped.map((c) => c.language.toUpperCase()).join(", ")} copy already exists. Use "Translate this copy" on it instead.`);
      }
      refreshCourseData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Translate failed", { id: toastId });
    }
  };

  const translateInPlace = async (course) => {
    try {
      await api.post(`/admin/content/courses/${course.id}/translate-in-place`);
      toast.success(`Translating "${course.title}"…`);
      refreshCourseData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Translate failed");
    }
  };

  const translating = schemaKey === "courses" && items.some((c) => ["queued", "running"].includes(c.translation?.status));
  useEffect(() => {
    if (!translating) return undefined;
    const id = setInterval(load, 3000);
    return () => { clearInterval(id); refreshCourseData(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translating, load]);

  // Drag reorder for nested lessons. We track the currently-dragged
  // lesson id + its source course_id (drops outside the same course are
  // rejected). After a valid drop we recompute `order` for every sibling
  // as (index+1)*10 and POST the batch to /admin/content/lessons/reorder.
  const [dragState, setDragState] = useState(null); // {id, courseId} | null
  const onLessonDragStart = (lesson) => setDragState({ id: lesson.id, courseId: lesson.course_id });
  const onLessonDrop = async (targetLesson) => {
    if (!dragState || dragState.id === targetLesson.id) { setDragState(null); return; }
    if (dragState.courseId !== targetLesson.course_id) {
      toast.error("Drag reorder only works within the same course.");
      setDragState(null);
      return;
    }
    const siblings = (lessonsByCourse.get(targetLesson.course_id) || []).slice();
    const fromIdx = siblings.findIndex((l) => l.id === dragState.id);
    const toIdx   = siblings.findIndex((l) => l.id === targetLesson.id);
    if (fromIdx < 0 || toIdx < 0) { setDragState(null); return; }
    const [moved] = siblings.splice(fromIdx, 1);
    siblings.splice(toIdx, 0, moved);
    // Reassign order = (i+1)*10 so future manual inserts have room in
    // between (100 → 110, no need to renumber everyone).
    const payload = siblings.map((l, i) => ({ id: l.id, order: (i + 1) * 10 }));
    // Optimistic UI: mutate local state immediately.
    setAllLessons((prev) => {
      const map = new Map(payload.map((p) => [p.id, p.order]));
      return prev.map((l) => (map.has(l.id) ? { ...l, order: map.get(l.id) } : l));
    });
    setDragState(null);
    try {
      await api.post("/admin/content/lessons/reorder", { items: payload });
      toast.success("Order updated.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
      // On failure, refetch to reconcile.
      api.get("/admin/content/lessons").then(({ data }) => setAllLessons(data || []));
    }
  };

  const lessonHandlers = {
    dragState,
    onDragStart: onLessonDragStart,
    onDragEnd: () => setDragState(null),
    onDrop: onLessonDrop,
    onEdit: setLessonEditing,
    onDuplicate: (l) => {
      const clone = { ...l };
      ["id", "created_at", "updated_at", "published_at"].forEach((k) => delete clone[k]);
      if (clone.title) clone.title = `${clone.title} (copy)`;
      setLessonEditing(clone);
    },
    onDelete: removeLesson,
  };

  const reorderChapters = async (courseId, order) => {
    const toastId = toast.loading("Moving chapter…");
    try {
      const { data } = await api.post(`/admin/content/courses/${courseId}/chapters/reorder`, { order, mirror: true });
      const extra = data.mirrored?.length ? ` · mirrored to ${data.mirrored.join(", ")}` : "";
      toast.success(`Chapters renumbered${extra}`, { id: toastId });
      if (data.skipped?.length) {
        toast.info(`Not mirrored to ${data.skipped.join(", ")} — that copy has a different chapter list.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Chapter move failed", { id: toastId });
    }
    reloadLessons();
  };

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [query, language]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((r) => (r.title || "").toLowerCase().includes(q)) : items;
  }, [items, query]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${schema.label.toLowerCase()} by title…`}
              data-testid={`admin-content-search-${schemaKey}`}
              className="h-9 w-64 pl-8 text-sm"
            />
          </div>
          <div className="text-sm text-slate-500" data-testid={`admin-content-count-${schemaKey}`}>
            {query ? `${filtered.length} of ${items.length}` : items.length} items
          </div>
          {hasLanguage && (
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid={`admin-content-language-filter-${schemaKey}`} className="h-9 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All languages</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="es">🇪🇸 Español</SelectItem>
                <SelectItem value="fr">🇫🇷 Français</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {(schemaKey === "courses" || schemaKey === "articles") && (
          <Button
            variant="outline"
            data-testid={`admin-auto-feature-${schemaKey}`}
            onClick={async () => {
              if (!window.confirm("Auto-flip 'Featured for Free' on the 2 top free-tier courses AND the 2 newest free-tier articles for this language? This resets any existing featured flags on other free items.")) return;
              try {
                const { data } = await api.post(`/admin/content/auto-feature-free?language=${language !== "all" ? language : "en"}`);
                toast.success(`Featured ${data.courses_featured.length} courses + ${data.articles_featured.length} articles for the Free shelf`);
                load();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Auto-feature failed");
              }
            }}
            className="border-amber-300 text-amber-800 hover:bg-amber-50 mr-2"
            title="One-click default: mark 2 lowest-order free courses + 2 newest free articles as Featured"
          >
            <Sparkles size={14} className="mr-1" /> Auto-pick free shelf
          </Button>
        )}
        <Button
          data-testid={TESTIDS.newBtn(schemaKey)}
          onClick={() => setEditing(emptyDoc())}
          className="bg-blue-800 text-white hover:bg-blue-700"
        >
          <Plus size={14} className="mr-1" /> {t("admin.content.new", { label: schema.label })}
        </Button>
      </div>

      {isCoursesTab ? (
        <div className="space-y-3" data-testid="admin-course-list">
          {pageItems.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl text-center py-12 text-slate-500 text-sm">
              {query ? "No courses match your search." : t("admin.content.emptyList")}
            </div>
          ) : pageItems.map((row) => (
            <CourseCard
              key={row.id}
              row={row}
              kids={lessonsByCourse.get(row.id) || []}
              isOpen={expanded.has(row.id)}
              onToggle={toggleExpand}
              onTranslate={translateCourse}
              onTranslateInPlace={translateInPlace}
              onEdit={setEditing}
              onDuplicate={duplicate}
              onDelete={remove}
              onAddLesson={addLessonToCourse}
              testIds={TESTIDS}
              lessonHandlers={lessonHandlers}
              onChapterReorder={reorderChapters}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/60">
                {schema.columns.map((c) => (
                  <th key={c} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 font-semibold whitespace-nowrap">
                    {c === "course_title" ? "Course" : c.replace(/_/g, " ")}
                  </th>
                ))}
                <th className="text-right px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  {t("admin.content.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr><td colSpan={schema.columns.length + 1} className="text-center py-12 text-slate-500 text-sm">
                  {query ? "No items match your search." : t("admin.content.emptyList")}
                </td></tr>
              ) : pageItems.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  {schema.columns.map((c) => {
                    const value = c === "course_title" ? (courseMap.get(row.course_id) || row.course_id || "—") : row[c];
                    const isTitle = c === "title" || c === "course_title";
                    return (
                      <td key={c} className={`px-4 py-3 text-slate-800 align-top ${isTitle ? "min-w-[240px] font-medium break-words" : ""}`}>
                        {value ?? "—"}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button data-testid={TESTIDS.editBtn} variant="ghost" onClick={() => setEditing(row)} className="text-slate-600 hover:text-blue-800 h-8 px-2" aria-label="Edit">
                      <Pencil size={14} />
                    </Button>
                    <Button data-testid={TESTIDS.duplicateBtn} variant="ghost" onClick={() => duplicate(row)} className="text-slate-600 hover:text-emerald-700 h-8 px-2" aria-label="Duplicate" title="Duplicate — clone this row and edit">
                      <Copy size={14} />
                    </Button>
                    <Button data-testid={TESTIDS.deleteBtn} variant="ghost" onClick={() => remove(row.id)} className="text-slate-600 hover:text-red-600 h-8 px-2" aria-label="Delete">
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminPager page={safePage} totalPages={totalPages} onChange={setPage} />

      {editing && (
        <EditorDialog
          schemaKey={schemaKey}
          schema={schema}
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {lessonEditing && (
        <EditorDialog
          schemaKey="lessons"
          schema={SCHEMAS.lessons}
          doc={lessonEditing}
          onClose={() => setLessonEditing(null)}
          onSaved={() => { setLessonEditing(null); reloadLessons(); }}
        />
      )}
    </div>
  );
}

function ChapterPicker({ value, existingChapters, disabled, onChange }) {
  // Three modes: "pick" (chose an existing chapter), "new" (typing a new
  // one), or "none" (no course selected yet so nothing to pick).
  //
  // When the admin picks an existing chapter from the dropdown, we patch
  // chapter_number + chapter_title + chapter_tier together so sibling
  // lessons always share identical values. When they create a new one,
  // we expose the three inputs so they're set at the same time.
  const [mode, setMode] = useState(() => {
    if (!value?.chapter_title && !value?.chapter_number) return "pick";
    // If the current value matches an existing chapter, start in pick mode.
    const match = existingChapters.find(
      (c) => c.chapter_number === value.chapter_number && c.chapter_title === value.chapter_title,
    );
    return match || existingChapters.length === 0 ? (match ? "pick" : "new") : "pick";
  });

  if (disabled) {
    return (
      <div className="mt-1 text-xs text-slate-500 italic px-3 py-2 bg-slate-50 border border-slate-200 rounded">
        Pick a course above first.
      </div>
    );
  }

  const pickValue = value?.chapter_title
    ? `${value.chapter_number}::${value.chapter_title}`
    : "";

  return (
    <div className="mt-1 space-y-2">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode("pick")}
          className={`text-xs font-semibold px-2.5 py-1 rounded ${mode === "pick" ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          Use existing chapter
        </button>
        <button
          type="button"
          onClick={() => setMode("new")}
          className={`text-xs font-semibold px-2.5 py-1 rounded ${mode === "new" ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          + New chapter
        </button>
      </div>

      {mode === "pick" ? (
        existingChapters.length === 0 ? (
          <div className="text-xs text-slate-500 italic px-3 py-2 bg-slate-50 border border-slate-200 rounded">
            No chapters exist in this course yet — switch to "+ New chapter" to create the first one.
          </div>
        ) : (
          <Select
            value={pickValue}
            onValueChange={(v) => {
              const match = existingChapters.find(
                (c) => `${c.chapter_number}::${c.chapter_title}` === v,
              );
              if (match) onChange(match);
            }}
          >
            <SelectTrigger data-testid="admin-chapter-picker-existing">
              <SelectValue placeholder="Select a chapter…" />
            </SelectTrigger>
            <SelectContent>
              {existingChapters.map((c) => (
                <SelectItem key={`${c.chapter_number}::${c.chapter_title}`} value={`${c.chapter_number}::${c.chapter_title}`}>
                  Ch {c.chapter_number} · {c.chapter_title || "(untitled)"} · {c.chapter_tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      ) : (
        <div className="grid grid-cols-[80px_1fr_120px] gap-2">
          <Input
            type="number"
            value={value?.chapter_number ?? ""}
            onChange={(e) => onChange({ chapter_number: e.target.value === "" ? "" : Number(e.target.value) })}
            placeholder="#"
            data-testid="admin-chapter-picker-new-number"
          />
          <Input
            type="text"
            value={value?.chapter_title ?? ""}
            onChange={(e) => onChange({ chapter_title: e.target.value })}
            placeholder="Chapter title (e.g. Fundamentals)"
            data-testid="admin-chapter-picker-new-title"
          />
          <Select
            value={value?.chapter_tier ?? "free"}
            onValueChange={(v) => onChange({ chapter_tier: v })}
          >
            <SelectTrigger data-testid="admin-chapter-picker-new-tier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["free", "basic", "premium"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function EditorDialog({ schemaKey, schema, doc, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(doc);
  const [saving, setSaving] = useState(false);
  const isNew = !doc.id;
  // If any field is a course-select, pre-load the courses list so the
  // dropdown has options. Filtered to the same language as the lesson
  // being edited (if set) so admins don't accidentally attach an
  // English lesson to a Spanish course.
  const hasCourseSelect = schema.fields.some((f) => f.type === "course-select");
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    if (!hasCourseSelect) return;
    api.get("/admin/content/courses").then(({ data }) => setCourses(data || [])).catch(() => {});
  }, [hasCourseSelect]);
  const courseOptions = useMemo(() => {
    if (!values.language) return courses;
    return courses.filter((c) => !c.language || c.language === values.language);
  }, [courses, values.language]);

  // If any field is a chapter-picker, load the sibling lessons for the
  // current course_id and extract the unique (chapter_number,
  // chapter_title, chapter_tier) tuples so admins can pick an existing
  // chapter instead of retyping all three fields and risking a typo.
  const hasChapterPicker = schema.fields.some((f) => f.type === "chapter-picker");
  const [siblingLessons, setSiblingLessons] = useState([]);
  useEffect(() => {
    if (!hasChapterPicker || !values.course_id) {
      setSiblingLessons([]);
      return;
    }
    api.get("/admin/content/lessons")
      .then(({ data }) => setSiblingLessons((data || []).filter(
        (l) => l.course_id === values.course_id && l.id !== doc.id,
      )))
      .catch(() => setSiblingLessons([]));
  }, [hasChapterPicker, values.course_id, doc.id]);
  const existingChapters = useMemo(() => {
    const map = new Map();
    siblingLessons.forEach((l) => {
      const key = `${l.chapter_number ?? "?"}::${l.chapter_title || ""}`;
      if (!map.has(key)) {
        map.set(key, {
          chapter_number: l.chapter_number ?? 1,
          chapter_title: l.chapter_title || "",
          chapter_tier: l.chapter_tier || "free",
        });
      }
    });
    return Array.from(map.values()).sort(
      (a, b) => (a.chapter_number ?? 0) - (b.chapter_number ?? 0),
    );
  }, [siblingLessons]);

  const set = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));
  const setMany = (patch) => setValues((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      // Build the outgoing payload — the FE stores number-field defaults
      // as empty strings while the input is untouched, but Pydantic on
      // the backend rejects "" for `Optional[int]` fields with a raw
      // `int_parsing` error. So for every number-typed field in the
      // schema, coerce a blank to `null` (Pydantic accepts that as
      // "no change" / "unset"). Strings & selects go through unchanged.
      const numberFields = new Set(
        schema.fields.filter((f) => f.type === "number").map((f) => f.name),
      );
      // Virtual UI-only fields that must never be sent to the backend.
      const virtualFields = new Set(
        schema.fields
          .filter((f) => ["chapter-picker", "section-header"].includes(f.type) || f.name.startsWith("__"))
          .map((f) => f.name),
      );
      const payload = {};
      for (const k of Object.keys(values)) {
        if (virtualFields.has(k)) continue;
        const v = values[k];
        if (numberFields.has(k)) {
          // Blank number → null (Pydantic treats missing/null the same
          // for `Optional[int]` fields, and required int fields fall
          // back to the field's default via the emptyDoc() seeding).
          payload[k] = v === "" || v === undefined ? null : Number(v);
        } else {
          payload[k] = v;
        }
      }
      // On PATCH, strip blank string values so we don't clobber existing
      // content with empty strings (a common trap when the FE schema has
      // more fields than the seeded document actually stores).
      if (!isNew) {
        for (const k of Object.keys(payload)) {
          if (payload[k] === "" || payload[k] === null) delete payload[k];
        }
      }
      // Audit fields the server manages — never sent.
      ["id", "created_at", "updated_at", "published_at"].forEach((k) => delete payload[k]);

      if (isNew) {
        await api.post(schema.endpoint, payload);
        toast.success(t("admin.content.created"));
      } else {
        await api.patch(`${schema.endpoint}/${doc.id}`, payload);
        toast.success(t("admin.content.saved"));
      }
      onSaved();
    } catch (e) {
      // Pydantic sends back an array of {loc, msg, type} objects for
      // 422 validation errors. Surface the FIRST one in plain English
      // so admins see "duration_minutes: must be an integer" instead
      // of "[object Object]".
      const detail = e.response?.data?.detail;
      let message = formatApiError(detail) || "Save failed";
      if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : "field";
        message = `${field}: ${first.msg}`;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
          <DialogTitle className="font-display text-2xl">
            {isNew ? t("admin.content.newTitle", { label: schema.label }) : t("admin.content.editTitle", { title: values.title || schemaKey })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {schema.fields.map((f) => {
            // Virtual field types that aren't actual DB columns.
            if (f.type === "hidden") return null;
            if (f.type === "section-header") {
              return (
                <div key={f.name} className="pt-3 mt-2 border-t border-slate-200 flex items-center gap-2">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    {f.label}
                  </div>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>
              );
            }
            return (
            <div key={f.name}>
              <Label className="text-slate-800 font-semibold text-sm">
                {f.label || f.name}
                {f.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {f.help && (
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">{f.help}</p>
              )}
              {f.type === "textarea" ? (
                <Textarea
                  value={values[f.name] ?? ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  rows={f.rows || 4}
                  className="mt-1"
                />
              ) : f.type === "toggle" ? (
                <label className="mt-1 flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!values[f.name]}
                    onChange={(e) => set(f.name, e.target.checked)}
                    className="w-4 h-4 accent-blue-800"
                  />
                  <span className="text-sm text-slate-600">Enable</span>
                </label>
              ) : f.type === "select" ? (
                <Select value={values[f.name] ?? f.default ?? ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "course-select" ? (
                <Select value={values[f.name] ?? ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select a course…" /></SelectTrigger>
                  <SelectContent>
                    {courseOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-500">No courses in this language yet</div>
                    ) : courseOptions.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}{c.language ? ` · ${c.language}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "chapter-picker" ? (
                <ChapterPicker
                  value={{
                    chapter_number: values.chapter_number,
                    chapter_title: values.chapter_title,
                    chapter_tier: values.chapter_tier,
                  }}
                  existingChapters={existingChapters}
                  disabled={!values.course_id}
                  onChange={(patch) => setMany(patch)}
                />
              ) : f.type === "attachments" ? (
                <AttachmentsField value={values[f.name]} onChange={(v) => set(f.name, v)} />
              ) : f.type === "file-upload" ? (
                <FileUploadField
                  value={values[f.name]}
                  onChange={(v) => set(f.name, v)}
                  onUploaded={(r) => setMany({ [f.name]: r.url, file_type: r.ext, ...(values.title ? {} : { title: r.filename.replace(/\.[^.]+$/, "") }) })}
                />
              ) : f.type === "image-upload" ? (
                <ImageUploadField
                  value={values[f.name] ?? ""}
                  onChange={(v) => set(f.name, v)}
                />
              ) : (
                <Input
                  type={f.type === "number" ? "number" : "text"}
                  value={values[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                  placeholder={f.placeholder}
                  className="mt-1"
                />
              )}
            </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-white shrink-0">
          <Button variant="outline" onClick={onClose} className="border-slate-300">
            {t("common.cancel")}
          </Button>
          <Button
            data-testid={TESTIDS.saveBtn}
            onClick={save}
            disabled={saving}
            className="bg-blue-800 text-white hover:bg-blue-700"
          >
            <Save size={14} className="mr-1" /> {saving ? t("common.saving") : (isNew ? t("common.create") : t("common.save"))}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
